/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Badge } from '@/components/ui/badge';
import { Building2, Package } from 'lucide-react';

export type PlaybookScope = 'organizational' | 'product';

interface ScopeBadgeProps {
  scope: PlaybookScope;
  showIcon?: boolean;
}

export function ScopeBadge({ scope, showIcon = true }: ScopeBadgeProps) {
  const config: Record<PlaybookScope, { label: string; icon: typeof Building2; className: string }> = {
    organizational: {
      label: 'Org',
      icon: Building2,
      className: 'bg-purple-100 text-purple-800 border-purple-200',
    },
    product: {
      label: 'Product',
      icon: Package,
      className: 'bg-blue-100 text-blue-800 border-blue-200',
    },
  };

  // Handle unexpected scope values gracefully
  const scopeConfig = config[scope] ?? config.organizational;
  const { label, icon: Icon, className } = scopeConfig;

  return (
    <Badge variant="outline" className={`text-xs ${className}`}>
      {showIcon && <Icon className="h-3 w-3 mr-1" />}
      {label}
    </Badge>
  );
}
